/**
 * @ Author: Housefolios
 * @ Create Time: 2021-12-01 15:08:58
 * @ Modified by: David Helmick
 * @ Modified time: 2025-08-12 14:58:48
 * @ Description: Wholesale Strategy Analysis page
 */

import React, { Suspense, lazy, useState } from 'react'
import {
  strategyBlockSpacerSx,
  strategyBorderMutedSx,
  strategyBorderRadius0Sx,
  strategyCardTitleSx,
  strategyDarkTextSx,
  strategyFont16Sx,
  strategyFullHeight100Sx,
  strategyFullWidthSx,
  strategyLinkBlueSx,
  strategyMarginBottom10Sx,
  strategyMarginTop25Sx,
  strategyMarginTop35Sx,
  strategyMetricGrayBgSx,
  strategyMetricRowFlexSx,
  strategyMetricRowSx,
  strategyMinHeight50Sx,
  strategyMutedDividerSx,
  strategyMutedPadXSx,
  strategyMutedTextSx,
  strategyNavyBgSx,
  calcDialogPaperSx,
  strategyPadBottom20FullSx,
  strategyPadBottom20Sx,
  strategyTableRowSx,
  strategyTextMuted802Sx,
  strategyWhiteBlueChipSx,
  strategyWhiteTextSx,
} from '@/housefolios-components/PropertyContainer/CenterPage/Common/strategyDashboardSx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import CountUp from '@/utils/SafeCountUp'
import {
  filter,
  map,
  round,
  isEmpty,
  includes,
  get,
  concat,
  max,
} from '@/utils/lodash'
const Chart = lazy(() => import('@/utils/SafeApexChart'))

import {
  Grid,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Dialog,
  Divider,
  Tooltip,
  Typography,
  Select,
  MenuItem,
  Box,
} from '@mui/material'

import ContactAgent from '../../../Marketplace/ContactAgent'
import GaugeChart from '@/utils/SafeGaugeChart'

import withSession from '@/housefolios-components/Session/withSession'
import { assumptionsOptions } from '@/housefolios-components/AssumptionsSettings/Accordion'
import { DEFAULT_ANALYTICS } from '@/housefolios-components/Property/fragments'
import { getStrategyName } from '@/utils/stratagies'
import {
  ContactInformation,
  getCashEntryFee,
  getCreativeEntryFee,
  getPitiTotal,
} from '@/utils/loanBalance'
import SubtoPITICash from '@/housefolios-components/Dialogs/SubtoPITI/Cash/'
import SubtoPITICreative from '@/housefolios-components/Dialogs/SubtoPITI/Creative/'
import CommentsDialog from '@/housefolios-components/Dialogs/Comments'
import {
  WholesaleNetProfitFromSaleTour,
  WholesaleStrategyTour,
  WholesaleTargetsTour,
  WholesaleWholesaleDetailsTour,
  WholesaleComparablePropertiesTour,
} from './tours'
import {
  ComparablePropertiesCard,
  DeactiveCard,
  StrategyCard,
  StrategyPageBase,
  TargetsCard,
  calculateCompData,
  createBasicChartOptions,
  createChartColors,
  createCurrencyFormat,
  createDonutChartOptions,
  createPercentageFormat,
  createStrategySummary,
  useStrategyPage,
} from '../Common'

import { LoadingSkeleton } from '@/housefolios-components/LoadingSkeleton'

function CenterPageWholesale(props) {
  const {
    property,
    propertyInput,
    analysisInput,
    assumptionsInput,
    renderInputFields,
  } = props

  const {
    modal1,
    maxOfferDescription,
    openStrategyTour,
    openTargetsTour,
    openNetProfitTour,
    openDetailsTour,
    openComparablePropertiesTour,
    toggle1,
    toggle1Close,
    toggleMaxOffer,
    toggleStrategyTour,
    toggleTargetsTour,
    toggleNetProfitTour,
    toggleDetailsTour,
    toggleComparablePropertiesTour,
    closeStrategyTour,
    closeTargetsTour,
    closeNetProfitTour,
    closeDetailsTour,
    closeComparablePropertiesTour,
  } = useStrategyPage({ strategyName: 'Wholesale'  })

  // const { activeOrg = {} } = props.session.me || {}
  const activeOrg = property?.portfolio?.organization
  const { acquisition = {}, assumptions = {} } = property
  const { analytics = DEFAULT_ANALYTICS, analysis = {} } = acquisition

  const currencyFormat = createCurrencyFormat()
  const percentageFormat = createPercentageFormat()
  const pieColors = createChartColors()

  const [dealSauceView, setDealSauceView] = useState('creative')
  const [openPITICashData, setOpenPITICashData] = useState(false)
  const [openPITICreativeData, setOpenPITICreativeData] =
    useState(false)

  const pitiTotal = getPitiTotal(property)
  const creativeEntryFee = getCreativeEntryFee(property)
  const cashEntryFee = getCashEntryFee(property)

  const rehabDollar = analytics.wholesale.rehabDollar //assumptions.rehabTier * sum(property.sqft)
  const maxOfferPrice1 =
    analytics.wholesale.arvTotalWholesale - rehabDollar
  const buyerProfitTotal =
    (assumptions.belowBuyerProfit / 100) * maxOfferPrice1
  const maxOfferPrice2 = maxOfferPrice1 - buyerProfitTotal
  const closingCostWholesaleDollar =
    analytics.wholesale.closingCostWholesaleDollar //assumptions.closingCostWholesaleType === '%' ? (assumptions.closingCostWholesale / 100) * maxOfferPrice2 : assumptions.closingCostWholesale
  const maxOfferPrice = maxOfferPrice2 - closingCostWholesaleDollar
  const assignmentFeeDollar = analytics.wholesale.assignmentFeeDollar //assumptions.assignmentFeeType === '%' ? (assumptions.assignmentFee / 100) * maxOfferPrice3 : assumptions.assignmentFee
  //const maxOfferPrice = maxOfferPrice3 - assignmentFeeDollar
  // const maxOfferPrice = analysisInput.resellPrice - (assumptions.rehabTier * sum(property.sqft)) - (assumptions.buyerProfitType === '%' ? (assumptions.buyerProfit / 100) * analysis.resellPrice : assumptions.buyerProfit) - (assumptions.closingCostWholesaleType === '%' ? (assumptions.closingCostWholesale / 100) * analysis.resellPrice : assumptions.closingCostWholesale) - (assumptions.assignmentFeeType === '%' ? (assumptions.assignmentFee / 100) * analysis.resellPrice : assumptions.assignmentFee)

  const pepMaxOfferPrice = Math.max(
    (includes(
      assumptionsInput.strategySpecificFields,
      'afterRepairValue',
    )
      ? analysisInput.afterRepairValueWholesale || 0
      : analysisInput.afterRepairValue) *
      (assumptionsInput.percentageOfARVWholesale / 100) -
      analytics.wholesale.rehabDollar,
    0,
  )
  // - assignmentFeeDollar

  const effectiveOfferPrice =
    analysisInput.pepAdjustedPriceWholesale || pepMaxOfferPrice

  const pepEstProfit =
    analytics.wholesale.arvTotalWholesale -
    (analytics.wholesale.arvTotalWholesale * 0.15 +
      analytics.wholesale.rehabDollar +
      pepMaxOfferPrice)

  const dispoPrice = pepMaxOfferPrice + assignmentFeeDollar

  const pepEstFlipProfit =
    analytics.wholesale.arvTotalWholesale -
    (pepMaxOfferPrice +
      assignmentFeeDollar +
      analytics.wholesale.rehabDollar +
      analytics.wholesale.arvTotalWholesale * 0.15)

  const pepMaxEstProfit = max([
    // pepEstFlipProfit,
    0.1 * analytics.wholesale.arvTotalWholesale,
    25000,
  ])

  const pepBigPictureFields = filter(
    [
      {
        type: 'dollar',
        name: 'listPrice',
        label: 'Asking Price',
        value:
          analysisInput.listPrice === null
            ? ''
            : analysisInput.listPrice.toString(),
        xs: 12,
        lg: 6,
        definition: 'listPrice',
        // tooltipField: 'The price that the seller is asking for this home. *Not necessarily the price that the investor will purchase the home for.',
        // tooltipButtonText: 'Asking Price Options',
        adornmentNumber: 1,
      },
      {
        type: 'dollar',
        name: includes(
          assumptionsInput.strategySpecificFields,
          'afterRepairValue',
        )
          ? 'afterRepairValueWholesale'
          : 'afterRepairValue',
        label: 'After Repair Value',
        // tooltipButtonText: 'Show ARV data',
        value: includes(
          assumptionsInput.strategySpecificFields,
          'afterRepairValue',
        )
          ? analysisInput.afterRepairValueWholesale === null
            ? ''
            : analysisInput.afterRepairValueWholesale.toString()
          : analysisInput.afterRepairValue === null
            ? ''
            : analysisInput.afterRepairValue.toString(),
        //helper: currencyFormat.format(analytics.fixFlip.equity) + ' in equity',
        xs: 12,
        lg: 6,
        definition: 'afterRepairValue',
        definitionTarget: includes(
          assumptionsInput.strategySpecificFields,
          'afterRepairValue',
        )
          ? 'afterRepairValueWholesale'
          : 'afterRepairValue',
        // tooltipField: 'The ARV is the value of a property after you have conducted repairs and are ready to sell',
        dataTour: 'acquisitionExpenses-step-6',
        dataTourDatabase: 'acquisitionExpenses-step-7',
        adornmentNumber: 2,
      },
      {
        type: 'select',
        name: 'rehabWholesaleType',
        label: 'Rehab Type',
        value: assumptionsInput.rehabWholesaleType,
        xs: 12,
        options: assumptionsOptions(activeOrg)?.rehabOptions,
        assumptions: true,
        adornmentNumber: 3,
      },
      {
        type: 'dollar',
        name: 'rehabCostWholesale',
        label: 'Est Rehab',
        value: analysisInput.rehabCostWholesale,
        // tooltipButtonText: 'Show Estimated Rehab data',
        xs: 12,
        lg: 6,
        definition: 'rehabCostWholesale',
        // tooltipField: 'An expense that is incurred when restoring an entity to its normal or near-normal condition.',
        hide:
          assumptionsInput.rehabWholesaleType !== 'singleRehabValue',
        dataTour: 'wholesaleDetails-step-4',
        adornmentNumber: 4,
      },
      {
        type: 'budgetLines',
        name: 'detailedRehabCostWholesale',
        label: 'Rehab Costs',
        value: analysisInput.detailedRehabCostWholesale,
        xs: 12,
        options: [
          'Appliances',
          'Cabinets/Vanities',
          'Carpet',
          'Countertops/Sinks/Toilets',
          'Driveway/Decking/Patio',
          'Electrical',
          'Exterior',
          'Flooring',
          'Framing',
          'Insulation/Drywall',
          'Landscaping',
          'Masonry',
          'Mechanical',
          'Molding/Tile/Trim',
          'Paint',
          'Plans/Permits',
          'Plumbing',
          'Roof',
          'Siding/Gutters',
          'Site Prep',
          'Staging',
          'Windows/Doors',
          'Other',
        ],
        hide: assumptionsInput.rehabWholesaleType !== 'detailedRehab',
        dataTour: 'wholesaleDetails-step-4',
        adornmentNumber: 4,
      },
      {
        type: 'rehabCalc',
        name: 'rehabCalcTotalWholesale',
        label: 'Rehab Calculator',
        value: analysisInput.rehabCalcTotalWholesale,
        xs: 12,
        lg: 6,
        property: property,
        assumptionsInput: assumptionsInput,
        affiliate: props.affiliate,
        hide: assumptionsInput.rehabWholesaleType !== 'rehabCalc',
        dataTour: 'wholesaleDetails-step-4',
        adornmentNumber: 4,
      },
      {
        type: 'select',
        name: 'rehabTierWholesale',
        label: 'Rehab Tier',
        value: assumptionsInput.rehabTierWholesale,
        options: filter(
          activeOrg.rehabTiers,
          (option) =>
            isEmpty(option.showMarkets) ||
            includes(
              option.showMarkets,
              analysisInput.marketType ||
                props.affiliate?.defaultMarketType,
            ),
        ),
        optionName: 'rehabTiers',
        memberOptions: filter(
          activeOrg.memberRehabTiers,
          (option) =>
            isEmpty(option.showMarkets) ||
            includes(
              option.showMarkets,
              analysisInput.marketType ||
                props.affiliate?.defaultMarketType,
            ),
        ),
        dynamicMemberOptions: map(
          filter(
            activeOrg.dynamicMemberRehabTiers,
            (option) =>
              isEmpty(option.showMarkets) ||
              includes(
                option.showMarkets,
                analysisInput.marketType ||
                  props.affiliate?.defaultMarketType,
              ),
          ),
          (option) => {
            let breakPointIndex = 0
            const arv = includes(
              assumptionsInput.strategySpecificFields,
              'afterRepairValue',
            )
              ? analysisInput.afterRepairValueWholesale === null
                ? null
                : analysisInput.afterRepairValueWholesale
              : analysisInput.afterRepairValue === null
                ? null
                : analysisInput.afterRepairValue
            while (
              option?.breakpoints[breakPointIndex] < arv &&
              breakPointIndex < option?.breakpoints.length
            ) {
              breakPointIndex++
            }
            return {
              ...option,
              label: `$${option.values[breakPointIndex]}/Sqft`,
            }
          },
        ),
        removeOption: true,
        xs: 12,
        lg: 6,
        assumptions: true,
        helper: currencyFormat.format(
          analytics.wholesale.rehabTierValueWholesale,
        ),
        //tooltipField: 'An expense that is incurred when restoring an entity to its normal or near-normal condition.',
        hide:
          assumptionsInput.rehabWholesaleType !== 'rehabTier' &&
          assumptionsInput.rehabWholesaleType !==
            'flippingAmericaRehab',
        dataTour: 'wholesaleDetails-step-4',
        adornmentNumber: 4,
      },
      {
        name: 'marketType',
        value: !analysisInput.marketType
          ? props.affiliate?.defaultMarketType
          : analysisInput.marketType,
        label: 'Market Type',
        options: concat(
          [props.affiliate?.defaultMarketType],
          props.affiliate?.marketTypes,
        ),
        type: 'select',
        xs: 12,
        lg: 6,
        hide:
          isEmpty(props.affiliate?.marketTypes) ||
          (assumptionsInput.rehabWholesaleType !== 'rehabTier' &&
            assumptionsInput.rehabWholesaleType !==
              'flippingAmericaRehab'),
      },
      {
        type: 'dollar',
        name: 'rehabDecadeCostWholesale',
        label: 'Rehab Per Decade',
        value: assumptionsInput.rehabDecadeCostWholesale,
        xs: 12,
        lg: 6,
        assumptions: true,
        hide:
          assumptionsInput.rehabWholesaleType !==
          'flippingAmericaRehab',
      },
      {
        type: 'percentage',
        name: 'rehabContingencyWholesale',
        value: assumptionsInput.rehabContingencyWholesale,
        adornmentToggle:
          assumptionsInput.rehabContingencyWholesaleType,
        adornment: assumptionsInput.rehabContingencyWholesaleType,
        adornmentName: 'rehabContingencyWholesaleType',
        helper:
          assumptionsInput.rehabContingencyWholesaleType === '%'
            ? currencyFormat.format(
                (property.sqft *
                  assumptionsInput.rehabTierWholesale *
                  assumptionsInput.rehabContingencyWholesale) /
                  100,
              )
            : null,
        label: 'Rehab Contingency',
        xs: 12,
        lg: 6,
        assumptions: true,
        dropdown: true,
        // tooltipField: 'Percentage of calculated rehab cost added as a saftey margin',
        // dataTour: "wholesaleDetails-step-5"
        adornmentNumber: 5,
      },
      {
        type: 'dropdown',
        name: 'assignmentFee',
        value: assumptionsInput.assignmentFee,
        label: 'Assignment Fee',
        adornmentToggle: assumptionsInput.assignmentFeeType,
        adornment: assumptionsInput.assignmentFeeType,
        adornmentName: 'assignmentFeeType',
        xs: 12,
        lg: 6,
        assumptions: true,
        dropdown: true,
        // tooltipField: 'The amount that you, the seller, expects to make on the wholesale property. Expressed as a dollar amount or percentage of ARV minus the rehab and closing costs.',
        dataTour: 'wholesaleDetails-step-7',
        adornmentNumber: 6,
      },
      {
        type: 'percentage',
        name: 'percentageOfARVWholesale',
        label: 'Percent of ARV',
        // tooltipButtonText: 'Show Purchase Price data',
        helper: currencyFormat.format(
          (includes(
            assumptionsInput.strategySpecificFields,
            'afterRepairValue',
          )
            ? analysisInput.afterRepairValueWholesale === null
              ? 0
              : Number(analysisInput.afterRepairValueWholesale)
            : analysisInput.afterRepairValue === null
              ? 0
              : Number(analysisInput.afterRepairValue)) *
            (assumptionsInput.percentageOfARVWholesale / 100),
        ),
        value: assumptionsInput.percentageOfARVWholesale,
        xs: 12,
        lg: 6,
        assumptions: true,
        // tooltipField: 'Percentage of ARV used to calculate Offer Price',
        adornmentNumber: 7,
      },
      {
        type: 'dollar',
        name: 'pepAdjustedPriceWholesale',
        label: 'Adjusted Purchase Price',
        // tooltipButtonText: 'Show Purchase Price data',
        value:
          analysisInput.pepAdjustedPriceWholesale?.toString() || '',
        xs: 12,
        lg: 6,
        // tooltipField: 'The price that the seller is asking for this home. *Not necessarily the price that the investor will purchase the home for.',
        adornmentNumber: 8,
      },
    ],
    (field) => !field.hide,
  )

  const wholesaleFields = filter(
    [
      {
        type: 'dollar',
        name: 'offerPrice',
        label: 'Purchase Price',
        tooltipButtonText: 'Show Purchase Price data',
        value:
          analysisInput.offerPrice === null
            ? //? analysisInput.listPrice.toString()
              ''
            : analysisInput.offerPrice.toString(),
        helper:
          !analysisInput.offerPrice ||
          analysisInput.offerPrice === '0'
            ? '0% Discount to Price'
            : (
                (1 -
                  analysisInput.offerPrice /
                    analysisInput.listPrice) *
                100
              ).toFixed(0) + '% Discount to Price',
        xs: 12,
        lg: 6,
        definition: 'offerPrice',
        tooltipField:
          'The price that the seller is asking for this home. *Not necessarily the price that the investor will purchase the home for.',
        dataTour: 'wholesaleDetails-step-1',
      },
      {
        type: 'dollar',
        name: includes(
          assumptionsInput.strategySpecificFields,
          'afterRepairValue',
        )
          ? 'afterRepairValueWholesale'
          : 'afterRepairValue',
        label: 'After Repair Value',
        tooltipButtonText: 'Show ARV data',
        value: includes(
          assumptionsInput.strategySpecificFields,
          'afterRepairValue',
        )
          ? analysisInput.afterRepairValueWholesale === null
            ? ''
            : analysisInput.afterRepairValueWholesale.toString()
          : analysisInput.afterRepairValue === null
            ? ''
            : analysisInput.afterRepairValue.toString(),
        //helper: currencyFormat.format(analytics.wholesale.equity) + ' in equity',
        xs: 12,
        lg: 6,
        definition: 'afterRepairValue',
        definitionTarget: includes(
          assumptionsInput.strategySpecificFields,
          'afterRepairValue',
        )
          ? 'afterRepairValueWholesale'
          : 'afterRepairValue',
        tooltipField:
          'The ARV is the value of a property after you have conducted repairs and are ready to sell',
        dataTour: 'wholesaleDetails-step-2',
      },
      {
        type: 'dollar',
        name: 'resellPriceWholesale',
        label: 'Resale Price',
        value:
          analysisInput.resellPriceWholesale === null
            ? ''
            : analysisInput.resellPriceWholesale.toString(),
        xs: 12,
        lg: 6,
        tooltipField:
          'The price you will resell the property for once the rehab is completed.',
        dataTour: 'wholesaleDetails-step-3',
      },
      {
        type: 'select',
        name: 'rehabWholesaleType',
        label: 'Rehab Type',
        value: assumptionsInput.rehabWholesaleType,
        xs: 12,
        options: assumptionsOptions(activeOrg)?.rehabOptions,
        assumptions: true,
      },
      {
        type: 'dollar',
        name: 'rehabCostWholesale',
        label: 'Est Rehab',
        value: analysisInput.rehabCostWholesale,
        tooltipButtonText: 'Show Estimated Rehab data',
        xs: 12,
        lg: 6,
        definition: 'rehabCostWholesale',
        tooltipField:
          'An expense that is incurred when restoring an entity to its normal or near-normal condition.',
        hide:
          assumptionsInput.rehabWholesaleType !== 'singleRehabValue',
        dataTour: 'wholesaleDetails-step-4',
      },
      {
        type: 'budgetLines',
        name: 'detailedRehabCostWholesale',
        label: 'Rehab Costs',
        value: analysisInput.detailedRehabCostWholesale,
        xs: 12,
        options: [
          'Appliances',
          'Cabinets/Vanities',
          'Carpet',
          'Countertops/Sinks/Toilets',
          'Driveway/Decking/Patio',
          'Electrical',
          'Exterior',
          'Flooring',
          'Framing',
          'Insulation/Drywall',
          'Landscaping',
          'Masonry',
          'Mechanical',
          'Molding/Tile/Trim',
          'Paint',
          'Plans/Permits',
          'Plumbing',
          'Roof',
          'Siding/Gutters',
          'Site Prep',
          'Staging',
          'Windows/Doors',
          'Other',
        ],
        hide: assumptionsInput.rehabWholesaleType !== 'detailedRehab',
        dataTour: 'wholesaleDetails-step-4',
      },
      {
        type: 'rehabCalc',
        name: 'rehabCalcTotalWholesale',
        label: 'Rehab Calculator',
        value: analysisInput.rehabCalcTotalWholesale,
        xs: 12,
        lg: 6,
        property: property,
        assumptionsInput: assumptionsInput,
        affiliate: props.affiliate,
        hide: assumptionsInput.rehabWholesaleType !== 'rehabCalc',
        dataTour: 'wholesaleDetails-step-4',
      },
      {
        type: 'select',
        name: 'rehabTierWholesale',
        label: 'Rehab Tier',
        value: assumptionsInput.rehabTierWholesale,
        options: filter(
          activeOrg.rehabTiers,
          (option) =>
            isEmpty(option.showMarkets) ||
            includes(
              option.showMarkets,
              analysisInput.marketType ||
                props.affiliate?.defaultMarketType,
            ),
        ),
        optionName: 'rehabTiers',
        memberOptions: filter(
          activeOrg.memberRehabTiers,
          (option) =>
            isEmpty(option.showMarkets) ||
            includes(
              option.showMarkets,
              analysisInput.marketType ||
                props.affiliate?.defaultMarketType,
            ),
        ),
        dynamicMemberOptions: map(
          filter(
            activeOrg.dynamicMemberRehabTiers,
            (option) =>
              isEmpty(option.showMarkets) ||
              includes(
                option.showMarkets,
                analysisInput.marketType ||
                  props.affiliate?.defaultMarketType,
              ),
          ),
          (option) => {
            let breakPointIndex = 0
            const arv = includes(
              assumptionsInput.strategySpecificFields,
              'afterRepairValue',
            )
              ? analysisInput.afterRepairValueWholesale === null
                ? null
                : analysisInput.afterRepairValueWholesale
              : analysisInput.afterRepairValue === null
                ? null
                : analysisInput.afterRepairValue
            while (
              option?.breakpoints[breakPointIndex] < arv &&
              breakPointIndex < option?.breakpoints.length
            ) {
              breakPointIndex++
            }
            return {
              ...option,
              label: `$${option.values[breakPointIndex]}/Sqft`,
            }
          },
        ),
        removeOption: true,
        xs: 12,
        lg: 6,
        assumptions: true,
        helper: currencyFormat.format(
          analytics.wholesale.rehabTierValueWholesale,
        ),
        //tooltipField: 'An expense that is incurred when restoring an entity to its normal or near-normal condition.',
        hide:
          assumptionsInput.rehabWholesaleType !== 'rehabTier' &&
          assumptionsInput.rehabWholesaleType !==
            'flippingAmericaRehab',
        dataTour: 'wholesaleDetails-step-4',
      },
      {
        name: 'marketType',
        value: !analysisInput.marketType
          ? props.affiliate?.defaultMarketType
          : analysisInput.marketType,
        label: 'Market Type',
        options: concat(
          [props.affiliate?.defaultMarketType],
          props.affiliate?.marketTypes,
        ),
        type: 'select',
        xs: 12,
        lg: 6,
        hide:
          isEmpty(props.affiliate?.marketTypes) ||
          (assumptionsInput.rehabWholesaleType !== 'rehabTier' &&
            assumptionsInput.rehabWholesaleType !==
              'flippingAmericaRehab'),
      },
      {
        type: 'dollar',
        name: 'rehabDecadeCostWholesale',
        label: 'Rehab Per Decade',
        value: assumptionsInput.rehabDecadeCostWholesale,
        xs: 12,
        lg: 6,
        assumptions: true,
        hide:
          assumptionsInput.rehabWholesaleType !==
          'flippingAmericaRehab',
      },
      {
        type: 'percentage',
        name: 'belowBuyerProfit',
        value: assumptionsInput.belowBuyerProfit,
        label: 'Min Buyer Profit',
        xs: 12,
        lg: 6,
        assumptions: true,
        tooltipField:
          'The targeted percentage profit the buyer expects.',
        dataTour: 'wholesaleDetails-step-5',
      },
      {
        type: 'dropdown',
        name: 'closingCostWholesale',
        value: assumptionsInput.closingCostWholesale,
        label: 'Closing Cost',
        adornmentToggle: assumptionsInput.closingCostWholesaleType,
        adornment: assumptionsInput.closingCostWholesaleType,
        adornmentName: 'closingCostWholesaleType',
        xs: 12,
        lg: 6,
        assumptions: true,
        dropdown: true,
        tooltipField:
          'The estimated closing cost the end buyer will have for a wholesale property. Expressed as a dollar amount or percentage of ARV minus the rehab.',
        dataTour: 'wholesaleDetails-step-6',
      },
      {
        type: 'dropdown',
        name: 'assignmentFee',
        value: assumptionsInput.assignmentFee,
        label: 'Assignment Fee',
        adornmentToggle: assumptionsInput.assignmentFeeType,
        adornment: assumptionsInput.assignmentFeeType,
        adornmentName: 'assignmentFeeType',
        xs: 12,
        lg: 6,
        assumptions: true,
        dropdown: true,
        tooltipField:
          'The amount that you, the seller, expects to make on the wholesale property. Expressed as a dollar amount or percentage of ARV minus the rehab and closing costs.',
        dataTour: 'wholesaleDetails-step-7',
      },
    ],
    (field) => !field.hide,
  )

  const extraPepProfit = pepMaxOfferPrice - effectiveOfferPrice
  const dataValues = [
    analytics.wholesale.arvTotalWholesale * 0.1,
    25000,
  ] // Replace with your actual data

  // Determine the colors dynamically
  // const sortedIndices =
  //   dataValues[0] > dataValues[1] ? [0, 1] : [1, 0]
  // const dynamicColors = sortedIndices.map((index) =>
  //   index === 0 ? '#1BC943' : '#AAEA16',
  // )

  const bigPepChartOptions = {
    chart: {
      type: 'bar',
      stacked: true,
      toolbar: {
        show: false, // Hides the menu
      },
    },
    plotOptions: {
      bar: {
        // distributed: true, // This ensures each bar gets a different color
      },
    },
    legend: {
      show: true, // Hides the legend
    },
    dataLabels: {
      enabled: false,
    },
    colors: ['#1BC943', extraPepProfit > 0 ? '#AAEA16' : '#F83244'],
    // colors: dynamicColors,
    // colors: ['#008FFB', '#00E396', '#FEB019'], // Applies per data point when `distributed: true`
    xaxis: {
      // categories: ['Est Profit', '10% of ARV', '$25,000'],
      categories: ['10% of ARV', '$25,000'],
      labels: {
        show: true, // Ensure it's enabled only once
        // style: { colors: ['#008FFB', '#00E396', '#FEB019'] },
      },
    },
    yaxis: {
      labels: {
        formatter: currencyFormat.format,
      },
    },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w  }) {
        return (
          '<div class="arrow_box">' +
          '<span>' +
          // ['Est Profit', '10% of ARV', '$25,000'][dataPointIndex] +
          ['10% of ARV', '$25,000'][dataPointIndex] +
          ': ' +
          currencyFormat.format(series[seriesIndex][dataPointIndex]) +
          '</span>' +
          '</div>'
        )
      },
    },
  }

  const bigPepChartSeries = [
    {
      name: 'Mimimum target profit',
      data: dataValues,
    },
    {
      name: 'Profit from adjusted price',
      data: [extraPepProfit, extraPepProfit],
    },
  ]

  const wholesaleChartOptions = createDonutChartOptions(
    currencyFormat,
    [
      'Rehab Amount',
      'Buyer Profit',
      'Closing Cost',
      'Assignment Fee',
    ],
    pieColors,
  )
  const wholesaleChartSeries = [
    rehabDollar,
    buyerProfitTotal,
    closingCostWholesaleDollar,
    assignmentFeeDollar || 0,
  ]

  const chartOptions = createBasicChartOptions(currencyFormat)
  const chartSeries = [
    {
      data: [
        round(analysis.resellPriceWholesale) || 0,
        round(analysis.offerPrice || analysis.listPrice) || 0,
        round(analytics?.wholesale?.saleProceedsWholesale) || 0,
      ],
      // colors: [
      //   ranges: [
      //     {
      //       from: 100,
      //       to: 0,
      //       color: '#f83145',
      //     },
      //   ],
      // ],
    },
  ]

  const calculationDialogs = [
    {
      value: pepMaxOfferPrice,
      label: 'Property Edge Pro - Max Offer Price',
      description: 'Highest Recommended Offer Price',
      calculation: (
        <>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                After Repair Value
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                {currencyFormat.format(
                  analytics.wholesale.arvTotalWholesale,
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                *Percentage of ARV
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                *
                {percentageFormat.format(
                  assumptions.percentageOfARVWholesale / 100,
                )}
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={strategyMutedDividerSx} />
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                Suggested Offer Price
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                {currencyFormat.format(
                  (analytics.wholesale.arvTotalWholesale *
                    assumptions.percentageOfARVWholesale) /
                    100,
                )}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -Rehab Costs
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -{currencyFormat.format(round(rehabDollar, 0))}
              </Typography>
            </Grid>
          </Grid>
          {/* <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={strategyMetricRowSx}
          >
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -10% of ARV
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyMutedTextSx}>
                -
                {currencyFormat.format(
                  analytics.wholesale.arvTotalWholesale * 0.1,
                )}
              </Typography>
            </Grid>
          </Grid> */}
          <Divider sx={strategyMutedDividerSx} />
          <Grid
            container
            sx={[strategyMetricRowSx, {
              alignItems: 'center',
              justifyContent: 'space-between',
            }]}
          >
            <Grid>
              <Typography sx={strategyWhiteTextSx}>
                Offer Price
              </Typography>
            </Grid>
            <Grid>
              <Typography sx={strategyWhiteTextSx}>
                {currencyFormat.format(pepMaxOfferPrice)}
              </Typography>
            </Grid>
          </Grid>
        </>
      ),
    },
  ]

  const pitiFields = [
    {
      type: 'dollar',
      label: 'Principal',
      name: 'pitiData.principal',
      value: propertyInput?.pitiData.principal,
      xs: 12,
      lg: 6,
      details: true,
    },
    {
      type: 'dollar',
      label: 'Interest',
      name: 'pitiData.interest',
      value: propertyInput?.pitiData.interest,
      xs: 12,
      lg: 6,
      details: true,
    },
    {
      type: 'dollar',
      label: 'Taxes',
      name: 'pitiData.taxes',
      value: propertyInput?.pitiData.taxes,
      xs: 12,
      lg: 6,
      details: true,
    },
    {
      type: 'dollar',
      label: 'Insurance',
      name: 'pitiData.insurance',
      value: propertyInput?.pitiData.insurance,
      xs: 12,
      lg: 6,
      details: true,
    },
    {
      type: 'text',
      label: 'Loan type',
      name: 'pitiData.loanType',
      value: propertyInput?.pitiData.loanType,
      xs: 12,
      lg: 6,
      details: true,
    },
    {
      type: 'datePicker',
      label: 'Origination Date',
      name: 'pitiData.originDate',
      value: new Date(propertyInput?.pitiData.originDate),
      xs: 12,
      lg: 6,
      details: true,
    },
    {
      name: 'pitiData.mortgageStatement',
      label: 'Mortgage statement',
      type: 'file',
      value: propertyInput?.pitiData.mortgageStatement,
      uploadFile: props.uploadFileProperty(
        'pitiData.mortgageStatement',
      ),
      // deleteFile: props.deleteFileProperty(),
      buttonText: 'Upload Mortgage statement',
      xs: 12,
      lg: 6,
      details: true,
    },
    {
      name: 'pitiData.insuranceStatement',
      label: 'Insurance statement',
      type: 'file',
      value: propertyInput?.pitiData.insuranceStatement,
      uploadFile: props.uploadFileProperty(
        'pitiData.insuranceStatement',
      ),
      // deleteFile: props.deleteFileProperty(),
      buttonText: 'Upload Insurance statement',
      xs: 12,
      lg: 6,
      details: true,
    },
  ]

  const strategySummary = createStrategySummary(
    analytics,
    'wholesale',
    'Wholesale',
    [
      {
        value: analytics.wholesale.saleProceedsWholesale || '-',
        arrow: analytics.wholesale.saleProceedsWholesaleArrow || 0,
        label: 'Net Profit',
        type: 'currency',
        target: assumptions.belowWholesaleSaleProceeds,
        targetLabel: 'Min Net Profit Target',
        targetName: 'belowWholesaleSaleProceeds',
        tourStep: 'targets-step-1',
        min: 0,
        max: 35000,
        minLabel: '$ 0',
        maxLabel: '$ 35k',
        step: 1000,
        valueLabelFormat: (x) =>
          x >= 1000 ? `${x / 1000}k` : `${x}`,
        description:
          'The amount of money left over after subtracting the Purchase Price from the Resale Price',
        calculation: (
          <>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  Resale Price
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {currencyFormat.format(
                    round(analysis.resellPriceWholesale, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Purchase Price
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(
                      analysis.offerPrice || analysis.listPrice,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={strategyMutedDividerSx} />
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  Net Profit
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  {currencyFormat.format(
                    round(
                      analytics.wholesale.saleProceedsWholesale,
                      0,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
          </>
        ),
      },
      {
        value: analytics.wholesale.buyerProfitTotal || '-',
        arrow: analytics.wholesale.buyerProfitArrow || 0,
        label: 'Buyer Profit',
        type: 'currency',
        target: buyerProfitTotal,
        targetLabel: 'Min Buyer Profit Target',
        targetName: 'belowBuyerProfit',
        tourStep: 'targets-step-2',
        min: 0,
        max: 30,
        description:
          'The amount of money left over for the End Buyer after subtracting Expenses from the ARV',
        calculation: (
          <>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  After Repair Value:
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  {currencyFormat.format(
                    analytics.wholesale.arvTotalWholesale,
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Rehab Cost:
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -{currencyFormat.format(rehabDollar)}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Resell Price:
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -
                  {currencyFormat.format(
                    round(analysis.resellPriceWholesale, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -Closing Cost:
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>
                  -{currencyFormat.format(closingCostWholesaleDollar)}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={strategyMutedDividerSx} />
            <Grid
              container
              sx={[strategyMetricRowSx, {
                alignItems: 'center',
                justifyContent: 'space-between',
              }]}
            >
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  Buyer Profit
                </Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyWhiteTextSx}>
                  {currencyFormat.format(
                    round(analytics.wholesale.buyerProfitTotal, 0),
                  )}
                </Typography>
              </Grid>
            </Grid>
            {/* 
            //SPECIFIC COMMENT: ASSIGNMENT FEE IS INCLUDED IN THE RESELL PRICE
            <Grid container alignItems="center" justifyContent="space-between" sx={strategyMetricRowSx}>
              <Grid>
                <Typography sx={strategyMutedTextSx}>Assignment Fee:</Typography>
              </Grid>
              <Grid>
                <Typography sx={strategyMutedTextSx}>{currencyFormat.format(assignmentFeeDollar)}</Typography>
              </Grid>
            </Grid> */}
          </>
        ),
      },
    ],
    {
      belowKey: 'belowTargetWholesale',
      onKey: 'onTargetWholesale',
      aboveKey: 'aboveTargetWholesale',
    },
  )

  const compData = calculateCompData({
    compValues: props.compValues,
    lowestAverage: props.lowestAverage,
  })

  const wholesaleCards = [
    {
      name: 'bigPicture',
      hide: !activeOrg.isRealeflow,
      card: (
        <Card variant="outlined" sx={{ width: '100%' }}>
          <Box sx={strategyMetricRowFlexSx}>
            <Box sx={{
              flexGrow: 1,
              minWidth: 0,
              "& > b": { display: "block" },
              "& > small": {
                textTransform: "uppercase",
                display: "block",
                opacity: 0.3,
                fontWeight: "bold",
              },
            }}>
              <b>Big Picture</b>
            </Box>
            {/* <div>
              <Tooltip title="Projected fund totals an investor will need to produce in order to acquire the property">
                <Button onClick={() => setOpenFixFlipFinancingTour(true)}>
                  <FontAwesomeIcon icon={['fal', 'info-circle']} />
                </Button>
              </Tooltip>
            </div> */}
          </Box>
          <Divider />
          <CardContent>
            {/* <p data-tour="financing-step-3">
              Based on your <b>{currencyFormat.format(analytics.fixFlip.allInCostFixFlip)}</b> <Tooltip title='The total amount of money this deal costs, including rehab, closing costs, points, etc.'><span onClick={() => toggle1('All-in Cost')} style={{ textDecoration: 'underline' }}>all-in cost</span></Tooltip>, you'll need to pay <b>{currencyFormat.format(analytics.fixFlip.totalOutOfPocketFixFlip)}</b> <Tooltip title='Total amount that the buyer has to cover with their own money.'><span onClick={() => toggle1('Total Out-of-Pocket')} style={{ textDecoration: 'underline' }}>out-of-pocket</span></Tooltip> to cover the downpayment, closing costs, and rehab for this property.
            </p> */}
            <Grid container>
              <Grid>
                <Grid
                  container
                  direction="row"
                  // alignItems="center"
                  spacing={1}
                >
                  {map(pepBigPictureFields, (section) =>
                    renderInputFields(section),
                  )}
                </Grid>
                {/* {!props.shared && (
                  <div>
                    <ContactAgent
                      property={property}
                      propertyPage={true}
                      canDrag={props.canDrag}
                      defaultOffer={effectiveOfferPrice}
                    />
                  </div>
                )} */}
              </Grid>
            </Grid>
            <Box sx={[{ borderRadius: '0.65rem', p: '0.5rem', mb: '0.5rem', backgroundColor: '#f8f9ff', textAlign: 'center', }, { borderWidth: "1px !important", borderStyle: "solid !important" }]} sx={strategyBorderMutedSx}>
              <Grid
                container

                onClick={() =>
                  toggle1('Property Edge Pro - Max Offer Price')
                }
                sx={{
                  display: 'flex',
                  backgroundColor: '#fff',
                  justifyContent: 'space-between',
                  py: '1rem',
                }}
              >
                <Grid
                  size={{ md: 6 }}

                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: '1rem',
                  }}
                >
                  <div data-tour="financing-step-1">
                    <Box sx={{ color: '#4f5062', pb: '0.25rem' }}>
                      Suggested Offer Price
                    </Box>
                    <Box sx={[{ display: 'flex', alignItems: 'center', justifyContent: 'center', }, { fontSize: "1.15rem !important" }, strategyDarkTextSx]}>
                      <Box
                        sx={{
                          display: 'flex',
                          p: '0.25rem',
                          alignItems: 'center',
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.3,
                          }}
                        >
                          <CountUp
                            start={0}
                            end={
                              pepMaxOfferPrice > 0
                                ? pepMaxOfferPrice
                                : 0
                            }
                            duration={1}
                            separator=","
                            decimals={0}
                            prefix="$"
                          />
                        </Box>
                      </Box>
                    </Box>
                  </div>
                </Grid>
                <Grid
                  size={{ md: 6 }}

                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: '1rem',
                  }}
                >
                  <div data-tour="financing-step-2">
                    <Box sx={{ color: '#4f5062', pb: '0.25rem' }}>
                      Strength of Offer
                    </Box>
                    <Box sx={[{ display: 'flex', alignItems: 'center', justifyContent: 'center', }, { fontSize: "1.15rem !important" }, strategyDarkTextSx]}>
                      <Box
                        sx={{
                          display: 'flex',
                          p: '0.25rem',
                          alignItems: 'center',
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.3,
                          }}
                        >
                          {analysis.listPrice ? (
                            <CountUp
                              start={0}
                              end={round(
                                (effectiveOfferPrice /
                                  analysis.listPrice || 0) * 100,
                                0,
                              )}
                              duration={1}
                              // delay={2}
                              separator=","
                              decimals={0}
                              suffix="%"
                            />
                          ) : (
                            <Typography>-</Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </div>
                </Grid>
                <Grid
                  size={{ md: 6 }}

                  sx={{ display: 'flex', justifyContent: 'center' }}
                >
                  <div data-tour="financing-step-2">
                    <Box sx={{ color: '#4f5062', pb: '0.25rem' }}>
                      Dispo Price
                    </Box>
                    <Box sx={[{ display: 'flex', alignItems: 'center', justifyContent: 'center', }, { fontSize: "1.15rem !important" }, strategyDarkTextSx]}>
                      <Box
                        sx={{
                          display: 'flex',
                          p: '0.25rem',
                          alignItems: 'center',
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.3,
                          }}
                        >
                          {analysis.listPrice ? (
                            <CountUp
                              start={0}
                              end={dispoPrice}
                              duration={1}
                              separator=","
                              decimals={0}
                              prefix="$"
                            />
                          ) : (
                            <Typography>-</Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </div>
                </Grid>
                <Grid
                  size={{ md: 6 }}

                  sx={{ display: 'flex', justifyContent: 'center' }}
                >
                  <div data-tour="financing-step-1">
                    <Box sx={{ color: '#4f5062', pb: '0.25rem' }}>
                      Estimated Flip Profit
                    </Box>
                    <Box sx={[{ display: 'flex', alignItems: 'center', justifyContent: 'center', }, { fontSize: "1.15rem !important" }, strategyDarkTextSx]}>
                      <Box
                        sx={{
                          display: 'flex',
                          p: '0.25rem',
                          alignItems: 'center',
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.3,
                          }}
                        >
                          <CountUp
                            start={0}
                            end={pepMaxEstProfit + extraPepProfit}
                            duration={1}
                            separator=","
                            decimals={0}
                            prefix="$"
                          />
                        </Box>
                      </Box>
                    </Box>
                  </div>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      ),
    },
    {
      name: 'bigPictureSummary',
      hide: !activeOrg.isRealeflow,
      card: (
        <Card variant="outlined" sx={{ width: '100%' }}>
          <Box sx={strategyTableRowSx}>
            <Box sx={{
              flexGrow: 1,
              minWidth: 0,
              "& > b": { display: "block" },
              "& > small": {
                textTransform: "uppercase",
                display: "block",
                opacity: 0.3,
                fontWeight: "bold",
              },
            }}>
              <b>Big Picture Summary</b>
            </Box>
            {/* <div>
              <Tooltip title="Quick glance determination seen on the main page">
                <Button onClick={() => setOpenFixFlipStrategyTour(true)}>
                  <FontAwesomeIcon icon={['fal', 'info-circle']} />
                </Button>
              </Tooltip>
            </div> */}
          </Box>
          <Divider />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', px: '1.5rem', pt: '1.5rem', pb: '1rem', }} />
            <Box
              component="span"
              data-tour-wholesale="gauge-step-1"

              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                justifyContent: 'center',
                px: '1.5rem',
                pt: '1.5rem',
                pb: '1rem',
              }}
            >
              <Box sx={[strategyPadBottom20Sx, { mx: 'auto', overflow: 'hidden' }]}>
                <GaugeChart
                  id="chartsGauges3B"
                  nrOfLevels={6}
                  colors={['#f83245', '#f4772e', '#1bc943']}
                  arcWidth={0.4}
                  hideText
                  percent={
                    effectiveOfferPrice / analysis.listPrice > 0.9
                      ? 1
                      : effectiveOfferPrice / analysis.listPrice < 0.7
                        ? 0
                        : (effectiveOfferPrice / analysis.listPrice -
                            0.7) *
                            ((5 * 2) / 3) +
                          1 / 6
                  }
                />
              </Box>
              <Box
                component="p" sx={[strategyMutedPadXSx, { mb: '1rem', textAlign: 'center' }]}>
                Your current Strength of Offer is{' '}
                {analysis.listPrice
                  ? `${round(
                      (effectiveOfferPrice / analysis.listPrice ||
                        0) * 100,
                      0,
                    )}
                  %${' '}`
                  : '-'}
              </Box>
            </Box>
            <Box
              component="span"
              data-tour-wholesale="gauge-step-2"

              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                justifyContent: 'center',
                px: '1.5rem',
                pt: '1.5rem',
                pb: '1rem',
              }}
            >
              <Box sx={[strategyPadBottom20FullSx, { mx: 'auto', overflow: 'hidden' }]}>
                <Suspense fallback={<LoadingSkeleton variant="chart" />}>
                  <Chart
                    options={bigPepChartOptions}
                    series={bigPepChartSeries}
                    type="bar"
                  />
                </Suspense>
              </Box>
              <Box
                component="p" sx={[strategyMutedPadXSx, { mb: '1rem', textAlign: 'center' }]}>
                {/* Between your Net Profit and 10% of the ARV,{' '}
                {pepEstFlipProfit >
                0.1 * analytics.wholesale.arvTotalWholesale
                  ? 'Net Profit'
                  : '10% of ARV'}{' '}
                is higher with a total of{' '}
                {currencyFormat.format(
                  max([
                    pepEstFlipProfit,
                    0.1 * analytics.wholesale.arvTotalWholesale,
                  ]),
                )}{' '} */}
                Check to see that the total profit is at least $25,000
                or 10% of ARV, whichever is higher.
              </Box>
            </Box>
            {!props.shared && (
              <Box sx={{ px: '1.5rem', pb: '1.5rem', pt: '0.5rem' }}>
                <ContactAgent
                  property={property}
                  propertyPage={true}
                  canDrag={props.canDrag}
                  defaultOffer={effectiveOfferPrice}
                />
              </Box>
            )}
          </Box>
        </Card>
      ),
    },
    {
      name: 'strategy',
      card: (
        <StrategyCard
          strategyName="Wholesale"
          activeOrg={activeOrg}
          percentage={strategySummary.percentage}
          onStrategyTourClick={toggleStrategyTour}
          shared={props.shared}
          property={property}
          canDrag={props.canDrag}
          contactAgent={
            !props.shared &&
            activeOrg.member !== 'gorillacapital' &&
            activeOrg.member !== 'paulmccomas' ? (
              <Box sx={{ px: '1.5rem', pb: '1.5rem', pt: '0.5rem' }}>
                <ContactAgent
                  property={property}
                  propertyPage={true}
                  canDrag={props.canDrag}
                />
              </Box>
            ) : null
          }
        >
          {activeOrg.member === 'paulmccomas' && (
            <Box sx={{ px: '1.5rem', pb: '1.5rem', pt: '0.5rem' }}>
              {property.jv ? (
                <Card variant="outlined">
                  <CardContent sx={{ px: '1.5rem', py: '1rem' }}>
                    <Box
                      sx={{
                        pb: '1rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      Property Submission Status
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                      }}
                    >
                      <Box component="span" sx={strategyWhiteBlueChipSx}>
                        {property.jvStatus}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ) : property.lender ? (
                <Card variant="outlined">
                  <CardContent sx={{ px: '1.5rem', py: '1rem' }}>
                    <Box
                      sx={{
                        pb: '1rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      Property Submission Status
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                      }}
                    >
                      <Box component="span" sx={strategyWhiteBlueChipSx}>
                        {property.lenderStatus}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <Button
                    fullWidth
                   
                    onClick={
                      !includes(
                        props.session.me.subscriptionPlan,
                        'growth',
                      ) &&
                      !includes(
                        props.session.me.subscriptionPlan,
                        'pro',
                      )
                        ? () => props.toggleLimit('paul')
                        : get(activeOrg, 'subscriptions.0.status') ===
                            'trialing'
                          ? () => props.setOpenEndTrial(true)
                          : strategySummary.percentage < 0.66 ||
                              property.images.length < 5
                            ? () =>
                                props.setOpenMemberDisclaimer(true)
                            : () => props.setOpenMember(true)
                    }
                  >
                    <Box component="span" sx={[strategyFont16Sx, {
                      display: "inline-block",
                      "&:not(:first-of-type)": { ml: "0.5rem", marginLeft: "0.5rem" },
                      "& + .btn-wrapper--icon": { ml: "0.5rem", marginLeft: "0.5rem" },
                    }]}>
                      Submit to Housefolios
                    </Box>
                  </Button>
                  <Card variant="outlined" sx={strategyBorderRadius0Sx}>
                    <CardContent sx={{ px: '1.5rem', py: '1rem' }}>
                      <Box
                        sx={{
                          pb: '1rem',
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        JV Requirements
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                        }}
                      >
                        <FontAwesomeIcon icon={[ 'far', property.images.length < 5 ? 'xmark' : 'check', ]} sx={[strategyLinkBlueSx, { fontSize: "1.38rem !important" }]} />
                        <Box sx={{ mx: '1.5rem' }}>
                          Upload at least 5 Images
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                        }}
                      >
                        <FontAwesomeIcon icon={['far', strategySummary.percentage < 0.66 ? 'xmark' : 'check']} sx={[strategyLinkBlueSx, { fontSize: "1.38rem !important" }]}/>
                        <Box sx={{ mx: '1.5rem' }}>Targets Met</Box>
                      </Box>
                    </CardContent>
                  </Card>
                </>
              )}
            </Box>
          )}
        </StrategyCard>
      ),
    },
    {
      name: 'targets',
      card: (
        <TargetsCard
          strategySummary={strategySummary}
          currencyFormat={currencyFormat}
          percentageFormat={percentageFormat}
          modal1={modal1}
          toggle1={toggle1}
          toggle1Close={toggle1Close}
          renderInputFields={renderInputFields}
          assumptionsInput={assumptionsInput}
          maxOfferPrice={maxOfferPrice}
          toggleMaxOffer={toggleMaxOffer}
          maxOfferDescription={maxOfferDescription}
          onTargetsTourClick={toggleTargetsTour}
          maxOfferCalculation={
            <>
              <Grid
                container
                sx={[strategyMetricRowSx, {
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }]}
              >
                <Grid>
                  <Typography sx={strategyMutedTextSx}>
                    After Repair Value:
                  </Typography>
                </Grid>
                <Grid>
                  <Typography sx={strategyMutedTextSx}>
                    {currencyFormat.format(
                      analytics.wholesale.arvTotalWholesale,
                    )}
                  </Typography>
                </Grid>
              </Grid>
              <Grid
                container
                sx={[strategyMetricRowSx, {
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }]}
              >
                <Grid>
                  <Typography sx={strategyMutedTextSx}>
                    -Rehab Cost
                  </Typography>
                </Grid>
                <Grid>
                  <Typography sx={strategyMutedTextSx}>
                    -{currencyFormat.format(rehabDollar)}
                  </Typography>
                </Grid>
              </Grid>
              <Grid
                container
                sx={[strategyMetricRowSx, {
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }]}
              >
                <Grid>
                  <Typography sx={strategyMutedTextSx}>
                    -Buyer Profit
                  </Typography>
                </Grid>
                <Grid>
                  <Typography sx={strategyMutedTextSx}>
                    -{currencyFormat.format(buyerProfitTotal)}
                  </Typography>
                </Grid>
              </Grid>
              <Grid
                container
                sx={[strategyMetricRowSx, {
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }]}
              >
                <Grid>
                  <Typography sx={strategyMutedTextSx}>
                    -Closing Cost:
                  </Typography>
                </Grid>
                <Grid>
                  <Typography sx={strategyMutedTextSx}>
                    -
                    {currencyFormat.format(
                      closingCostWholesaleDollar,
                    )}
                  </Typography>
                </Grid>
              </Grid>
              <Divider sx={strategyMutedDividerSx} />
              <Grid
                container
                sx={[strategyMetricRowSx, {
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }]}
              >
                <Grid>
                  <Typography sx={strategyWhiteTextSx}>
                    Max Offer
                  </Typography>
                </Grid>
                <Grid>
                  <Typography sx={strategyWhiteTextSx}>
                    {currencyFormat.format(round(maxOfferPrice, 0))}
                  </Typography>
                </Grid>
              </Grid>
            </>
          }
        />
      ),
    },
    {
      name: 'netProfit',
      card: (
        <Card variant="outlined" sx={{ width: '100%' }}>
          <Box sx={strategyTableRowSx}>
            <Box sx={{
              flexGrow: 1,
              minWidth: 0,
              "& > b": { display: "block" },
              "& > small": {
                textTransform: "uppercase",
                display: "block",
                opacity: 0.3,
                fontWeight: "bold",
              },
            }}>
              <small
                sx={{
                  display: 'block',
                  textTransform: 'uppercase',
                  mt: '0.25rem',
                }}
              >
                Summary
              </small>
              <b>Net Profit from Sale</b>
            </Box>
            <div>
              <Tooltip title="Breakdown of the resale price minus expenses to show net profit">
                <Button
                  onClick={() => toggleNetProfitTour()}
                 
                  sx={{
                    backgroundColor: "rgba(54, 140, 255, 0.15)",
                    color: "#368cff",
                    borderColor: "transparent",
                    "&:hover, &.active": { backgroundColor: "#368cff", color: "#fff" },
                    "&:hover .btn-wrapper--icon svg path, &.active .btn-wrapper--icon svg path": { fill: "#fff" },
                    height: "30px !important",
                    width: "30px !important",
                    lineHeight: "30px !important",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 0,
                    border: 0,
                    textAlign: "left",
                  }}
                >
                  <FontAwesomeIcon icon={['fal', 'info-circle']} />
                </Button>
              </Tooltip>
            </div>
          </Box>
          <Divider />
          <CardContent>
            <Grid>
              <Box sx={[{ borderRadius: '0.65rem', p: '0.5rem', mb: '0.5rem', backgroundColor: '#f8f9ff', textAlign: 'left', }, { borderWidth: "1px !important", borderStyle: "solid !important" }]} sx={strategyBorderMutedSx}>
                <Grid container sx={[strategyTextMuted802Sx, { fontSize: "0.875rem !important" }]}>
                  <Grid
                    size={{ md: 4 }}
                    data-tour="netProfitFromSale-step-1"

                    sx={{ display: 'flex', justifyContent: 'flex-start' }}
                  >
                    <div>
                      <Box sx={{ color: '#4f5062', pb: '0.25rem' }}>
                        Resale Price
                      </Box>
                      <Box sx={[{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', }, { fontSize: "1.15rem !important" }, strategyDarkTextSx]}>
                        <Box
                          sx={{
                            display: 'flex',
                            p: '0.25rem',
                            alignItems: 'center',
                          }}
                        >
                          <Box
                           
                            sx={{
                              display: "inline-block",
                              py: 0,
                              px: "0.7em",
                              fontSize: "70%",
                              fontWeight: 700,
                              lineHeight: "21px",
                              textAlign: "center",
                              whiteSpace: "nowrap",
                              verticalAlign: "baseline",
                              borderRadius: "100%",
                              textTransform: "uppercase",
                              height: 10,
                              color: "#fff",
                              backgroundColor: "#1bc943",
                              textIndent: "-999rem",
                              width: 10,
                              p: 0,
                              boxShadow: "0 0 0 2px #fff",
                              mr: "0.5rem",
                            }}
                          >
                            fast
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              whiteSpace: 'nowrap',
                              lineHeight: 1.3,
                            }}
                          >
                            <CountUp
                              start={0}
                              end={
                                analysis.resellPriceWholesale / 1000
                              }
                              duration={1}
                              delay={2}
                              decimals={1}
                              decimal="."
                              prefix="$"
                              suffix="k"
                            />
                          </Box>
                        </Box>
                      </Box>
                    </div>
                  </Grid>
                  <Grid
                    size={{ md: 4 }}
                    data-tour="netProfitFromSale-step-2"

                    sx={{ display: 'flex', justifyContent: 'flex-start' }}
                  >
                    <div>
                      <Box sx={{ color: '#4f5062', pb: '0.25rem' }}>
                        Expenses
                      </Box>
                      <Box sx={[{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', }, { fontSize: "1.15rem !important" }, strategyDarkTextSx]}>
                        <Box
                          sx={{
                            display: 'flex',
                            p: '0.25rem',
                            alignItems: 'center',
                          }}
                        >
                          <Box
                           
                            sx={{
                              display: "inline-block",
                              py: 0,
                              px: "0.7em",
                              fontSize: "70%",
                              fontWeight: 700,
                              lineHeight: "21px",
                              textAlign: "center",
                              whiteSpace: "nowrap",
                              verticalAlign: "baseline",
                              borderRadius: "100%",
                              textTransform: "uppercase",
                              height: 10,
                              color: "#fff",
                              backgroundColor: "#f83245",
                              textIndent: "-999rem",
                              width: 10,
                              p: 0,
                              boxShadow: "0 0 0 2px #fff",
                              mr: "0.5rem",
                            }}
                          >
                            normal
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              whiteSpace: 'nowrap',
                              lineHeight: 1.3,
                            }}
                          >
                            <CountUp
                              start={0}
                              end={
                                (analysis.offerPrice ||
                                  analysis.listPrice) / 1000
                              }
                              duration={1}
                              delay={2}
                              decimals={1}
                              decimal="."
                              prefix="$"
                              suffix="k"
                            />
                          </Box>
                        </Box>
                      </Box>
                    </div>
                  </Grid>
                  <Grid
                    size={{ md: 4 }}
                    data-tour="netProfitFromSale-step-3"

                    sx={{ display: 'flex', justifyContent: 'flex-start' }}
                  >
                    <div>
                      <Box sx={{ color: '#4f5062', pb: '0.25rem' }}>
                        Net Profit
                      </Box>
                      <Box sx={[{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', }, { fontSize: "1.15rem !important" }, strategyDarkTextSx]}>
                        <Box
                          sx={{
                            display: 'flex',
                            p: '0.25rem',
                            alignItems: 'center',
                          }}
                        >
                          <Box
                           
                            sx={{
                              display: "inline-block",
                              py: 0,
                              px: "0.7em",
                              fontSize: "70%",
                              fontWeight: 700,
                              lineHeight: "21px",
                              textAlign: "center",
                              whiteSpace: "nowrap",
                              verticalAlign: "baseline",
                              borderRadius: "100%",
                              textTransform: "uppercase",
                              height: 10,
                              color: "#fff",
                              backgroundColor: "#368cff",
                              textIndent: "-999rem",
                              width: 10,
                              p: 0,
                              boxShadow: "0 0 0 2px #fff",
                              mr: "0.5rem",
                            }}
                          >
                            slow
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              whiteSpace: 'nowrap',
                              lineHeight: 1.3,
                            }}
                          >
                            <CountUp
                              start={0}
                              end={
                                analytics.wholesale
                                  .saleProceedsWholesale / 1000
                              }
                              duration={1}
                              delay={2}
                              separator=","
                              decimals={0}
                              prefix="$"
                              suffix="k"
                            />
                          </Box>
                        </Box>
                      </Box>
                    </div>
                  </Grid>
                </Grid>
              </Box>
              <>
                <Suspense fallback={<LoadingSkeleton variant="chart" />}>
                  <Chart
                    options={chartOptions}
                    series={chartSeries}
                    type="bar"
                    height={280}
                  />
                </Suspense>
              </>
            </Grid>
          </CardContent>
        </Card>
      ),
    },
    {
      name: 'wholesale',
      card: (
        <Card variant="outlined" sx={{ width: '100%' }}>
          <Box sx={strategyMetricRowFlexSx}>
            <Box sx={{
              flexGrow: 1,
              minWidth: 0,
              "& > b": { display: "block" },
              "& > small": {
                textTransform: "uppercase",
                display: "block",
                opacity: 0.3,
                fontWeight: "bold",
              },
            }}>
              <b>
                {getStrategyName('Wholesale', activeOrg?.member)}{' '}
                Details
              </b>
            </Box>
            <div>
              <Tooltip title="Estimated expenses allocated to the renovations of a property">
                <Button
                  onClick={() => toggleDetailsTour()}
                 
                  sx={{
                    backgroundColor: "rgba(54, 140, 255, 0.15)",
                    color: "#368cff",
                    borderColor: "transparent",
                    "&:hover, &.active": { backgroundColor: "#368cff", color: "#fff" },
                    "&:hover .btn-wrapper--icon svg path, &.active .btn-wrapper--icon svg path": { fill: "#fff" },
                    height: "30px !important",
                    width: "30px !important",
                    lineHeight: "30px !important",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 0,
                    border: 0,
                    textAlign: "left",
                  }}
                >
                  <FontAwesomeIcon icon={['fal', 'info-circle']} />
                </Button>
              </Tooltip>
            </div>
          </Box>
          <Divider />
          <CardContent>
            <Grid>
              <Grid>
                <Box
                  sx={{ display: 'flex', justifyContent: 'center' }}
                >
                  <Suspense fallback={<LoadingSkeleton variant="chart" />}>
                    <Chart
                      options={wholesaleChartOptions}
                      series={wholesaleChartSeries}
                      type="donut"
                      width="300"
                    />
                  </Suspense>
                </Box>
              </Grid>
              <Divider sx={strategyBlockSpacerSx} />
              <Grid>
                <Grid
                  container
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  {map(wholesaleFields, (field) =>
                    renderInputFields(field),
                  )}
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ),
    },
    {
      name: 'comparableProperties',
      card: (
        <ComparablePropertiesCard
          basicView={props.basicView}
          compData={compData}
          onComparablePropertiesTourClick={
            toggleComparablePropertiesTour
          }
          onToggleCenter={props.toggleCenter}
          determinedComps={props.determinedComps}
          compProperties={props.compProperties}
        />
      ),
    },
    {
      name: 'piti',
      hide:
        activeOrg?.member !== 'pacemorby' &&
        activeOrg?.member !== 'astroflip',
      card: props.basicView ? null : (
        <Card variant="outlined" sx={{ width: '100%' }}>
          <Box sx={strategyMetricRowFlexSx}>
            <Box sx={{
              flexGrow: 1,
              minWidth: 0,
              "& > b": { display: "block" },
              "& > small": {
                textTransform: "uppercase",
                display: "block",
                opacity: 0.3,
                fontWeight: "bold",
              },
            }}>
              <b>Deal Sauce Summary</b>
            </Box>
            <Select
              value={dealSauceView}
              name="dealSauceView"
              onChange={(event) =>
                setDealSauceView(event.target.value)
              }
              sx={strategyFullWidthSx}
            >
              <MenuItem value="cash" key="cash">
                Cash
              </MenuItem>
              <MenuItem value="creative" key="creative">
                Creative
              </MenuItem>
            </Select>
          </Box>
          <Divider />
          <CardContent>
            {dealSauceView === 'cash' && (
              <>
                <Box sx={[{ borderRadius: '0.65rem', p: '0.5rem', mb: '0.5rem', backgroundColor: '#f8f9ff', textAlign: 'center', }, { borderWidth: "1px !important", borderStyle: "solid !important" }]} sx={strategyBorderMutedSx}>
                  <Grid container sx={[strategyTextMuted802Sx, { fontSize: "0.875rem !important" }]}>
                    <Grid
                      size={{ md: 6 }}

                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <div>
                        <Box sx={{ color: '#4f5062', pb: '0.25rem' }}>
                          Entry Fee
                        </Box>
                        <Box sx={[{ display: 'flex', alignItems: 'center', justifyContent: 'center', }, { fontSize: "1.15rem !important" }, strategyDarkTextSx]}>
                          <Box
                            sx={strategyMinHeight50Sx}
                            sx={{
                              display: 'flex',
                              p: '0.25rem',
                              alignItems: 'center',
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                whiteSpace: 'nowrap',
                                lineHeight: 1.3,
                              }}
                            >
                              <CountUp
                                start={0}
                                end={cashEntryFee}
                                duration={0}
                                separator=","
                                delay={1}
                                decimals={0}
                                decimal="."
                                prefix="$"
                              />
                            </Box>
                          </Box>
                        </Box>
                      </div>
                    </Grid>
                    <Grid
                      size={{ md: 6 }}

                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <div>
                        <Box sx={{ color: '#4f5062', pb: '0.25rem' }}>
                          Max Allowable Offer
                        </Box>
                        <Box sx={[{ display: 'flex', alignItems: 'center', justifyContent: 'center', }, { fontSize: "1.15rem !important" }, strategyDarkTextSx]}>
                          <Box
                            sx={strategyMinHeight50Sx}
                            sx={{
                              display: 'flex',
                              p: '0.25rem',
                              alignItems: 'center',
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                whiteSpace: 'nowrap',
                                lineHeight: 1.3,
                              }}
                            >
                              <CountUp
                                start={0}
                                end={
                                  analysis.afterRepairValue -
                                  cashEntryFee
                                }
                                duration={0}
                                separator=","
                                delay={1}
                                decimals={0}
                                decimal="."
                                prefix="$"
                              />
                            </Box>
                          </Box>
                        </Box>
                      </div>
                    </Grid>
                  </Grid>
                </Box>
                <Grid size={12} sx={strategyMarginTop35Sx}>
                  <Grid container direction="column" spacing={4}>
                    <ContactInformation property={property} />
                  </Grid>
                </Grid>
                <CommentsDialog
                  property={property}
                  userId={props?.session?.me?._id}
                />
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                 
                  onClick={(e) => setOpenPITICashData(true)} sx={strategyMarginTop25Sx}>
                  <Typography>View Cash Underwriting</Typography>
                </Button>
              </>
            )}
            {dealSauceView === 'creative' && (
              <>
                <Box sx={[{ borderRadius: '0.65rem', p: '0.5rem', mb: '0.5rem', backgroundColor: '#f8f9ff', textAlign: 'center', }, { borderWidth: "1px !important", borderStyle: "solid !important" }]} sx={strategyBorderMutedSx}>
                  <Grid container sx={[strategyTextMuted802Sx, { fontSize: "0.875rem !important" }]}>
                    <Grid
                      size={{ md: 6 }}

                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <div>
                        <Box sx={{ color: '#4f5062', pb: '0.25rem' }}>
                          Entry Fee
                        </Box>
                        <Box sx={[{ display: 'flex', alignItems: 'center', justifyContent: 'center', }, { fontSize: "1.15rem !important" }, strategyDarkTextSx]}>
                          <Box
                            sx={{
                              display: 'flex',
                              p: '0.25rem',
                              alignItems: 'center',
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                whiteSpace: 'nowrap',
                                lineHeight: 1.3,
                              }}
                            >
                              <CountUp
                                start={0}
                                end={creativeEntryFee}
                                duration={1}
                                separator=","
                                delay={1}
                                decimals={0}
                                decimal="."
                                prefix="$"
                              />
                            </Box>
                          </Box>
                        </Box>
                      </div>
                    </Grid>
                    <Grid
                      size={{ md: 6 }}

                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <div>
                        <Box sx={{ color: '#4f5062', pb: '0.25rem' }}>
                          PITI
                        </Box>
                        <Box sx={[{ display: 'flex', alignItems: 'center', justifyContent: 'center', }, { fontSize: "1.15rem !important" }, strategyDarkTextSx]}>
                          <Box
                            sx={{
                              display: 'flex',
                              p: '0.25rem',
                              alignItems: 'center',
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                whiteSpace: 'nowrap',
                                lineHeight: 1.3,
                              }}
                            >
                              <CountUp
                                start={0}
                                end={pitiTotal}
                                duration={1}
                                separator=","
                                delay={1}
                                decimals={0}
                                decimal="."
                                prefix="$"
                              />
                            </Box>
                          </Box>
                        </Box>
                      </div>
                    </Grid>
                    <Grid
                      size={{ md: 12 }}

                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <div>
                        <Box sx={{ color: '#4f5062', pb: '0.25rem' }}>
                          % of Purchase Price
                        </Box>
                        <Box sx={[{ display: 'flex', alignItems: 'center', justifyContent: 'center', }, { fontSize: "1.15rem !important" }, strategyDarkTextSx]}>
                          <Box
                            sx={{
                              display: 'flex',
                              p: '0.25rem',
                              alignItems: 'center',
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                whiteSpace: 'nowrap',
                                lineHeight: 1.3,
                              }}
                            >
                              <CountUp
                                start={0}
                                end={
                                  analysis?.offerPrice &&
                                  analysis.offerPrice !== 0
                                    ? (creativeEntryFee /
                                        analysis.offerPrice) *
                                      100
                                    : 0
                                }
                                duration={1}
                                delay={1}
                                decimals={2}
                                decimal="."
                                suffix="%"
                              />
                            </Box>
                          </Box>
                        </Box>
                      </div>
                    </Grid>
                  </Grid>
                </Box>
                <Grid container>
                  <Grid size={12}>
                    <Grid container direction="column" spacing={4}>
                      <ContactInformation property={property} />
                    </Grid>
                  </Grid>
                  <CommentsDialog
                    property={property}
                    userId={props?.session?.me?._id}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                   
                    onClick={(e) => setOpenPITICreativeData(true)} sx={strategyMarginTop25Sx}>
                    <Typography>
                      View Creative Underwriting
                    </Typography>
                  </Button>
                </Grid>
              </>
            )}
          </CardContent>
        </Card>
      ),
    },
    {
      name: 'deactive',
      card: <DeactiveCard />,
    },
  ]

  return (
    <>
      <StrategyPageBase
        cards={wholesaleCards}
        strategyName="Wholesale"
        strategyKey="wholesale"
        property={property}
        activeOrg={activeOrg}
        canDrag={props.canDrag}
        publicDashboard={props.publicDashboard}
        publicImageCard={props.publicImageCard}
        session={props.session}
        renderInputFields={renderInputFields}
      />

      {map(calculationDialogs, (section) => (
        <Dialog
          key={section.label}
          open={modal1 === section.label}
          onClose={toggle1Close}
          maxWidth="lg"
          scroll="body"
          slotProps={{ paper: { sx: calcDialogPaperSx } }}
        >
          {/* <BlockUi blocking={loader} loader={<PacmanLoader color={'var(--primary)'} loading={true} />}> */}
          <Grid container spacing={0}>
            <Grid size={5}>
              <Box sx={[{ height: '100%', borderRadius: '0.65rem' }, {
                position: "relative",
                display: "flex",
                flexDirection: "column",
                "& > .divider": { zIndex: 7, width: "100%" },
                "& .hero-footer": { zIndex: 7 },
                "& .header-nav-wrapper": { zIndex: 27 },
                "& .bg-composed-wrapper--image": { backgroundRepeat: "no-repeat", backgroundSize: "cover", backgroundPosition: "center center" },
                borderTopLeftRadius: "inherit",
                borderTopRightRadius: "inherit",
                alignItems: "center",
                ".MuiCard-root > &": {
                  top: -1,
                  marginLeft: -1,
                  marginRight: -1,
                  marginBottom: -2,
                  zIndex: 6,
                },
                backgroundImage: "linear-gradient(135deg, var(--hf-calc-hero-from, #CE9FFC) 10%, var(--hf-calc-hero-to, #7367F0) 100%) !important",
                  "html[data-color-mode=\"dark\"] & .MuiTypography-root": { color: "rgba(255, 255, 255, 0.92) !important" },
              }, {
                "@media (min-width: 1440px)": { borderTopRightRadius: "0 !important", borderBottomRightRadius: "0 !important" },
              }]}>
                <Box
                  sx={{
                    flexGrow: 1,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={[strategyNavyBgSx, { borderRadius: '0.65rem' }, {
                    position: "absolute",
                    left: 0,
                    top: 0,
                    height: "100%",
                    width: "100%",
                    zIndex: 5,
                    opacity: "0.7 !important",
                  }, {
                    "@media (min-width: 1440px)": { borderTopRightRadius: "0 !important", borderBottomRightRadius: "0 !important" },
                  }]}/>
                  <Box sx={[{ justifyContent: 'center', textAlign: 'center', p: '3rem', '@media (min-width: 1440px)': { justifyContent: 'flex-start', textAlign: 'left' }, }, { position: "relative", zIndex: "7", width: "100%" }]}>
                    <Box sx={{ color: '#fff', mt: '1rem' }}>
                      <Typography sx={strategyCardTitleSx}>
                        Calculation
                      </Typography>
                    </Box>
                    {section.calculation}
                    <Box sx={{ color: '#fff', mt: '1rem' }}>
                      <Typography>
                        * Calculation may be a few dollars off due to
                        rounding
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid size={7}>
              <div>
                <Card>
                  <CardHeader title={section.label} />
                  <Divider />
                  <CardContent sx={strategyFullHeight100Sx}>
                    <Grid
                      container
                      direction="row"
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Grid size={12} sx={strategyMarginBottom10Sx}>
                        <Card sx={strategyMetricGrayBgSx}>
                          <CardContent>
                            <Typography>
                              {section.description}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </CardContent>
                  <Divider />
                  <CardActions>
                    <Grid
                      container
                      sx={{
                        justifyContent: 'center',
                      }}
                    >
                      <Grid>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={toggle1Close}
                        >
                          Close
                        </Button>
                      </Grid>
                    </Grid>
                  </CardActions>
                </Card>
              </div>
            </Grid>
          </Grid>
          {/* </BlockUi> */}
        </Dialog>
      ))}
      <SubtoPITICash
        open={openPITICashData}
        onClose={() => setOpenPITICashData(false)}
        propertyId={property?._id}
        address={property?.address}
        city={property.city}
        state={property.state}
        zipcode={property?.zip}
      />
      <SubtoPITICreative
        open={openPITICreativeData}
        onClose={() => setOpenPITICreativeData(false)}
        propertyId={property?._id}
        address={property?.address}
        city={property.city}
        state={property.state}
        zipcode={property?.zip}
      />

      <WholesaleStrategyTour
        isOpenWholesaleStrategyTour={openStrategyTour}
        closeWholesaleStrategyTour={closeStrategyTour}
      />
      <WholesaleTargetsTour
        isOpenWholesaleTargetsTour={openTargetsTour}
        closeWholesaleTargetsTour={closeTargetsTour}
      />
      <WholesaleNetProfitFromSaleTour
        isOpenWholesaleNetProfitFromSaleTour={openNetProfitTour}
        closeWholesaleNetProfitFromSaleTour={closeNetProfitTour}
      />
      <WholesaleWholesaleDetailsTour
        isOpenWholesaleWholesaleDetailsTour={openDetailsTour}
        closeWholesaleWholesaleDetailsTour={closeDetailsTour}
      />
      <WholesaleComparablePropertiesTour
        isOpenWholesaleComparablePropertiesTour={
          openComparablePropertiesTour
        }
        closeWholesaleComparablePropertiesTour={
          closeComparablePropertiesTour
        }
      />
    </>
  )
}

export default withSession(CenterPageWholesale)
